import * as React from 'react';
import { graphql } from 'gatsby';
import Img from 'gatsby-image';
import { rem } from 'polished';
import { goToTarget, useScrollLock } from '../browsing';
import { cx } from "@emotion/css";

import type { LinkType } from '../link';
import {
  linkToId,
  linkToString,
} from '../link';
import { carouselContext, useInitNavigation } from '../carouselContext';

import { styled } from './themeContext';
import Link from './Link';
import LanguageSelector from './globalNavigation/LanguageSelector';
import { ReactComponent as CloseButtonSvg } from './globalNavigation/closeButton.svg';
import { ReactComponent as DividerSvg } from './globalNavigation/divider.svg';
import { ReactComponent as FacebookIconSvg } from './globalNavigation/facebookLogo.svg';
import { ReactComponent as InstagramIconSvg } from './globalNavigation/instagramLogo.svg';
import { ReactComponent as TwitterIconSvg } from './globalNavigation/twitterLogo.svg';
import { ReactComponent as NaverCafeIconSvg } from './globalNavigation/naverCafeLogo.svg';
import { ReactComponent as YoutubeIconSvg } from './globalNavigation/youtubeLogo.svg';
import hamburgerSvgUrl from './globalNavigation/hamburger.svg';

type NavigationEntry = {
  label: string,
  link: LinkType,
};

type SocialEntry = {
  service: NonNullable<GatsbyTypes.Footer_socialLinksFragment['snsLinks'][number]>['service'],
  entry: {
    link: LinkType,
    label: string,
  },
};

type GlobalNavigationProps = {
  logoImage: GatsbyTypes.GlobalNavigation_logoImageFragment,
  flatLogoImage: GatsbyTypes.GlobalNavigation_flatLogoImageFragment,
  entries: NavigationEntry[],
  homeLink: LinkType,
  socials: SocialEntry[],
  currentLocale: string,
};

const Container = styled.div(props => ({
  [props.theme.media['lg']]: {
    position: 'relative',
    zIndex: 1,
  },
}));

const Nav = styled.nav((props) => ({
  '@media (max-height: 28em)': {
    justifyContent: 'initial',
  },
  'display': 'flex',
  'zIndex': 2,
  'flexDirection': 'column',
  'alignItems': 'center',
  'justifyContent': 'center',
  'position': 'fixed',
  'overflowY': 'auto',
  'top': 0,
  'left': 0,
  'width': '100vw',
  'height': '100vh',
  'minHeight': '100vh',
  'boxSizing': 'border-box',
  'padding': `${rem(30)} 0`,
  'backgroundColor': 'rgba(17, 17, 17, 0.95)',
  'transform': 'translateX(-100%)',
  'transition': 'transform .5s ease',
  '&.active': {
    transform: 'translateX(0)',
  },
  [props.theme.media['lg']]: {
    'display': 'flex',
    'flexDirection': 'row',
    'position': 'static',
    'height': props.theme.navigationHeight,
    'minHeight': 'auto',
    'transition': 'none',
    'background': 'none',
    'overflowY': 'hidden',
    'transform': 'none',
    'fontFamily': 'inherit',
    'backgroundColor': 'rgba(0, 0, 0, 0.7)',
    'padding': `${rem(30)} 0`,
    'marginBottom': rem(-77),
    'width': 'auto',
    '&.fixed': {
      position: 'fixed',
      top: 0,
      width: '100vw',
    },
  },
}));

const CloseButton = styled.button((props) => ({
  'position': 'fixed',
  'width': rem(35),
  'height': rem(35),
  'left': rem(15),
  'top': rem(15),
  'cursor': 'pointer',
  'border': 'none',
  'padding': 0,
  'background': 'transparent',
  'transform': 'translateX(-100%)',
  'transition': 'transform .5s ease',
  '&.active': {
    transform: 'translateX(0)',
  },
  [props.theme.media['sm']]: {
    'width': rem(45),
    'height': rem(45),
    'top': rem(35),
    '&.active': {
      left: rem(27),
    },
  },
  [props.theme.media['md']]: {
    width: rem(50),
    height: rem(50),
  },
  [props.theme.media['lg']]: {
    display: 'none',
  },
}));

const HomeLink = styled(Link)((props) => ({
  [props.theme.media['lg']]: {
    marginLeft: rem(83),
  },
}));

const LogoImgContainer = styled.div((props) => ({
  display: 'none',
  width: rem(125),
  height: rem(55),
  [props.theme.media['lg']]: {
    display: 'inline-block',
  },
}));

const MobileLogoImgContainer = styled.div((props) => ({
  display: 'inline-block',
  width: rem(161),
  height: rem(97),
  marginBottom: rem(20),
  marginTop: rem(20),
  [props.theme.media['sm']]: {
    marginBottom: rem(30),
  },
  [props.theme.media['md']]: {
    width: rem(200),
    height: rem(120),
    marginBottom: rem(40),
  },
  [props.theme.media['lg']]: {
    display: 'none',
  },
}));

const HamburgerButton = styled.button((props) => ({
  zIndex: 1,
  position: 'fixed',
  top: rem(11),
  left: rem(8),
  border: 'none',
  cursor: 'pointer',
  backgroundColor: 'transparent',
  [props.theme.media['sm']]: {
    top: rem(24),
    left: rem(15),
  },
  [props.theme.media['lg']]: {
    display: 'none',
  },
}));

const HamburgerButtonImg = styled.img((props) => ({
  width: rem(32),
  height: rem(28),
  [props.theme.media['sm']]: {
    width: rem(41),
    height: rem(34),
  },
}));

const NavList = styled.ul((props) => ({
  listStyle: 'none',
  padding: 0,
  margin: 0,
  width: 'auto',
  height: 'auto',
  ...props.theme.typography.eventHeader,
  [props.theme.media['lg']]: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-around',
    width: '100%',
    maxWidth: rem(1200),
    fontFamily: 'inherit',
  },
}));

const NavListItem = styled.li((props) => ({
  margin: `${rem(20)} 0`,
  textAlign: 'center',
  ':lang(zh-Hant), :lang(ja)': {
    margin: `${rem(16)} 0`,
  },
  [props.theme.media['md']]: {
    margin: `${rem(40)} 0`,
    ':lang(zh-Hant), :lang(ja)': {
      margin: `${rem(32)} 0`,
    },
  },
  [props.theme.media['lg']]: {
    margin: 0,
    ':lang(zh-Hant), :lang(ja)': {
      margin: 0,
    },
  },
}));

const NavListItemLink = styled(Link)((props) => ({
  color: '#fff',
  textDecoration: 'none',
  fontSize: rem(25),
  [props.theme.media['md']]: {
    fontSize: rem(40),
  },
  [props.theme.media['lg']]: {
    'fontSize': rem(18),
    fontWeight: 'bold',
    ':hover': {
      color: '#FFE81E',
      background: 'linear-gradient(94.1deg, #FFF8A6 -0.37%, #FFE81E 62.65%, #FFAA00 99.63%)',
      WebkitBackgroundClip: 'text',
      MozBackgroundClip: 'transparent',
      WebkitTextFillColor: 'transparent',
      MozTextFillColor: 'text',
    },
  },
}));

const DividerWrapper = styled.div((props) => ({
  margin: `${rem(10)} 0 ${rem(20)} 0`,
  [props.theme.media['sm']]: {
    margin: `${rem(10)} 0 ${rem(40)} 0`,
  },
  [props.theme.media['md']]: {
    margin: `${rem(10)} 0 ${rem(50)} 0`,
  },
  [props.theme.media['lg']]: {
    display: 'none',
  },
}));

const Divider = styled(DividerSvg)((props) => ({
  width: rem(171),
  height: rem(10),
  [props.theme.media['sm']]: {
    width: rem(226),
    height: rem(12),
  },
}));

const SocialList = styled.ul((props) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  padding: 0,
  margin: 0,
  width: 'auto',
  height: 'auto',
  [props.theme.media['lg']]: {
    display: 'none',
  },
}));

const SocialItem = styled.li({
  'listStyle': 'none',
  ':not(:last-child)': {
    marginRight: rem(20),
  },
});

const FacebookIcon = styled(FacebookIconSvg)((props) => ({
  width: rem(16),
  height: rem(30),
  [props.theme.media['sm']]: {
    width: rem(21),
    height: rem(38),
  },
}));

const InstagramIcon = styled(InstagramIconSvg)((props) => ({
  width: rem(27),
  height: rem(27),
  [props.theme.media['sm']]: {
    width: rem(37),
    height: rem(37),
  },
}));

const TwitterIcon = styled(TwitterIconSvg)((props) => ({
  width: rem(26),
  height: rem(21),
  [props.theme.media['sm']]: {
    width: rem(40),
    height: rem(28),
  },
}));

const NaverCafeIcon = styled(NaverCafeIconSvg)((props) => ({
  width: rem(26),
  height: rem(26),
  [props.theme.media['sm']]: {
    width: rem(35),
    height: rem(35),
  },
}));

const YoutubeIcon = styled(YoutubeIconSvg)((props) => ({
  width: rem(30),
  height: rem(24),
  [props.theme.media['sm']]: {
    width: rem(43),
    height: rem(30),
  },
}));

const LanguageSelectorWrapper = styled.div((props) => ({
  marginTop: rem(20),
  marginBottom: rem(60),
  [props.theme.media['sm']]: {
    marginTop: rem(30),
  },
  [props.theme.media['md']]: {
    marginTop: rem(40),
  },
  [props.theme.media['lg']]: {
    marginTop: 0,
    marginBottom: 0,
  },
}));

const GlobalNavigation: React.FC<GlobalNavigationProps> = ({
  logoImage,
  flatLogoImage,
  entries,
  homeLink,
  socials,
  currentLocale,
}) => {
  const { setPlayCarousel } = React.useContext(carouselContext);
  const [menuOpened, setMenuOpened] = React.useState(false);
  const handleClickHamburger = React.useCallback(() => {
    setMenuOpened((prevMenuOpend) => !prevMenuOpend);
    setPlayCarousel((prevSetPlayCarousel) => !prevSetPlayCarousel);
  }, []);
  const [gameNavigationPassed, setGameNavigationPassed] = React.useState(false);
  React.useEffect(() => {
    window?.addEventListener('scroll', checkPassingGameNavigation);
    return () => {
      window?.removeEventListener('scroll', checkPassingGameNavigation);
    };
  }, []);
  const checkPassingGameNavigation = () => {
    if (window?.pageYOffset > 40) setGameNavigationPassed(true);
    else setGameNavigationPassed(false);
  };
  useScrollLock(menuOpened);
  useInitNavigation(menuOpened, setMenuOpened);
  return (
    <Container>
      <HamburgerButton onClick={handleClickHamburger}>
        <HamburgerButtonImg src={hamburgerSvgUrl} />
      </HamburgerButton>
      <Nav className={cx(menuOpened && 'active', gameNavigationPassed && 'fixed')}>
        <CloseButton
          onClick={handleClickHamburger}
          className={menuOpened ? 'active' : ''}
        >
          <CloseButtonSvg />
        </CloseButton>
        <HomeLink
          link={homeLink}
          onClick={(e) => {
            const targetId = linkToId(homeLink);
            if (targetId) {
              goToTarget({
                targetId,
                beforeScroll: () => {
                  e.preventDefault();
                  setMenuOpened(false);
                },
              });
            }
          }}
        >
          {flatLogoImage.fixed && (
            <LogoImgContainer>
              <Img fixed={flatLogoImage.fixed} loading="auto" />
            </LogoImgContainer>
          )}
          {logoImage.fluid && (
            <MobileLogoImgContainer>
              <Img fluid={logoImage.fluid} loading="auto" />
            </MobileLogoImgContainer>
          )}
        </HomeLink>
        <NavList>
          {entries.map((menu) => (
            <NavListItem key={linkToString(menu.link)}>
              <NavListItemLink
                link={menu.link}
                onClick={(e) => {
                  const targetId = linkToId(menu.link);
                  if (targetId) {
                    goToTarget({
                      targetId,
                      beforeScroll: () => {
                        e.preventDefault();
                        setMenuOpened(false);
                      },
                    });
                  }
                }}
              >
                {menu.label}
              </NavListItemLink>
            </NavListItem>
          ))}
        </NavList>
        <DividerWrapper>
          <Divider />
        </DividerWrapper>
        <SocialList>
          {socials.map((social) => {
            const socialComponent = (() => {
              switch (social.service) {
                case 'TWITTER':
                  return <TwitterIcon />;
                case 'INSTAGRAM':
                  return <InstagramIcon />;
                case 'FACEBOOK':
                  return <FacebookIcon />;
                case 'NAVER_CAFE':
                  return <NaverCafeIcon />;
                case 'YOUTUBE':
                  return <YoutubeIcon />;
              }
            })();
            return (
              <SocialItem key={linkToString(social.entry.link)}>
                <Link link={social.entry.link}>{socialComponent}</Link>
              </SocialItem>
            );
          })}
        </SocialList>
        <LanguageSelectorWrapper>
          <LanguageSelector
            currentLocale={currentLocale}
          />
        </LanguageSelectorWrapper>
      </Nav>
    </Container>
  );
};

export default GlobalNavigation;

export const fragments = graphql`
  fragment GlobalNavigation_navigation_entries on SiteInformation {
    navigationItems {
      label
      url
    }
  }

  fragment GlobalNavigation_logoImage on ImageSharp {
    fluid(maxWidth: 640, srcSetBreakpoints: [120, 320, 640]) {
      ...GatsbyImageSharpFluid_withWebp_noBase64
    }
  }

  fragment GlobalNavigation_flatLogoImage on ImageSharp {
    fixed(height: 55) {
      ...GatsbyImageSharpFixed_withWebp_noBase64
    }
  }
`;
